// src/hooks/useContainerWidth.js
import api from "../config/axiosConfig";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { convertToFormData } from "../config/convertToFormData";
import { useSelector } from "react-redux";
import { actionType } from "../utils/constant";

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function useTemplate() {
  const { profile } = useSelector((state) => state.profile);
  const { permissions } = profile;

  const [selectedModal, setSelectedModal] = useState(null);
  const [createModalDialogue, setCreateModalDialogue] = useState(false);
  const [allTemplates, setAllTemplates] = useState();
  const [carausalCollapsed, setCarausalCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 1000); // 500ms debounce delay
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteSelectedModel, setDeleteSelectedModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 5,
    totalPages: 1,
    total: 0,
  });

  const handleSubmit = async (values) => {
    if (!permissions.actions.includes(actionType.template_create)) {
      toast.error("You don't have permission to create templates");
      return;
    }
    const res = await createTemplateAPI({
      model_id: values.model_id,
      component_name: values.component_name,
      images: values.images,
      items: values.items,
    });
    if (res.success) {
      setCreateModalDialogue(false);
      const data = res?.data?.data?.data[0];
      const dataToSend = {
        component_template_id: data?.component_template_id,
        model_id: data?.model?.model_id,
        model_name: data?.model?.model_name,
      };
      onTemplateClick(dataToSend);
      fetchTemplatesAPI();
      return { success: true };
    }
    return { success: false };
  };

  const handleClose = () => {
    setCreateModalDialogue(false);
  };

  const onTemplateDelete = async () => {
    if (!permissions.actions.includes(actionType.template_delete)) {
      toast.error("You don't have permission to delete templates");
      return;
    }
    await deleteTemplateAPI(deleteSelectedModel.component_template_id);
    setDeleteModal(false);
    fetchTemplatesAPI();
  };

  const createTemplateClick = () => {
    if (!permissions.actions.includes(actionType.template_create)) {
      toast.error("You don't have permission to create template");
      return;
    }
    setSelectedModal(null);
    setCreateModalDialogue(true);
  };

  const onTemplateClick = (template) => {
    if (!permissions.actions.includes(actionType.template_view)) {
      toast.error("You don't have permission to view templates");
      return;
    }

    setSelectedModal(template);
    setCarausalCollapsed(true);
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const tableDataCreation = (datas) => {
    const updatedList = datas.map((data) => {
      return {
        component_template_id: data?.component_template_id || "",
        model_name: data?.model?.model_name || "",
        model_id: data?.model?.model_id || "",
        created_at: data?.created_at || "",
        no_of_components: data?.template_components?.length || 0,
      };
    });

    return updatedList;
  };

  const createTemplateAPI = async (data) => {
    if (!permissions.actions.includes(actionType.template_create)) {
      toast.error("You don't have permission to create templates");
      return;
    }

    const formData = convertToFormData(data);
    return await api
      .post(`/api/rfi-component-templates/`, formData)
      .then((res) => {
        toast.success(res?.data?.message || "Template created successfully!");
        return { success: true, data: res };
      })
      .catch((err) => {
        console.error("err", err);
        toast.error(err?.response?.data?.message || "Something went wrong!");
        return { success: false, error: err };
      });
  };

  const deleteTemplateAPI = async (templateId) => {
    if (!permissions.actions.includes(actionType.template_delete)) {
      toast.error("You don't have permission to delete templates");
      return;
    }
    return await api
      .delete(`/api/rfi-component-templates/${templateId}`)
      .then((res) => {
        toast.success(res?.data?.message || "Template deleted successfully!");
        return res;
      })
      .catch((err) => {
        console.error("err", err);
        toast.error(err?.response?.data?.message || "Something went wrong!");
        return err;
      });
  };

  const fetchTemplatesAPI = useCallback(async () => {
    if (!permissions.actions.includes(actionType.template_view)) {
      toast.error("You don't have permission to view templates");
      return;
    }
    setLoading(true);
    return await api
      .get(`/api/rfi-component-templates/`, {
        params: {
          ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
          page: pagination.page,
          perPage: pagination.perPage,
        },
      })
      .then((res) => {
        const updatedComponents = tableDataCreation(res?.data?.data);
        setAllTemplates(updatedComponents);
        setPagination((prev) => ({
          ...prev,
          total: res.data.count,
          totalPages: res.data.totalPages,
          page: res.data.currentPage,
          perPage: res.data.perPage,
        }));
        setLoading(false);
        return res;
      })
      .catch((err) => {
        console.error("err", err);
        setLoading(false);
        return err;
      });
  }, [
    pagination.page,
    pagination.perPage,
    debouncedSearchTerm,
    permissions.actions,
  ]);

  useEffect(() => {
    if (!permissions.actions.includes(actionType.template_view)) {
      toast.error("You don't have permission to view templates");
    } else {
      fetchTemplatesAPI();
    }
  }, [
    fetchTemplatesAPI,
    pagination.page,
    pagination.perPage,
    debouncedSearchTerm,
    permissions.actions,
  ]);

  return {
    selectedModal,
    createModalDialogue,
    allTemplates,
    carausalCollapsed,
    handleSubmit,
    onTemplateDelete,
    createTemplateClick,
    onTemplateClick,
    setCarausalCollapsed,
    setSearchTerm,
    pagination,
    handlePageChange,
    deleteModal,
    setDeleteModal,
    deleteSelectedModel,
    setDeleteSelectedModel,
    loading,
    fetchTemplatesAPI,
    handleClose,
    permissions,
  };
}
